import type { Joint, JointUUID, Vector2, Vector3, Vector5 } from "../../types/scene";
import { generateUuid } from "../../utils/id";

export interface JointParsingState {
  angleUnit: "radian" | "degree";
  jointClassDefaults: Record<string, Record<string, unknown> | undefined>;
  jointDefaultScope: Record<string, unknown>;
  resolvedJointElementNames: WeakMap<Element, string>;
}

export type ParsedJointData = {
  axis?: number[];
  range?: number[];
  position?: Vector3;
  armature?: number;
  damping?: number;
  frictionloss?: number;
  stiffness?: number;
  ref?: number;
  springref?: number;
  limited?: boolean | "auto";
  limitSolverReference?: Vector2;
  limitSolverImpedance?: Vector5;
  user?: string;
};
type JointWithMjcfMetadata = Joint & {
  mjcfName?: string;
  align?: string;
};
const normalizeNumberArray = (value: unknown): number[] | undefined => {
  if (typeof value === "number") return [value];
  if (!Array.isArray(value)) return undefined;
  const values = value.map((entry) => Number(entry)).filter((entry) => Number.isFinite(entry));
  return values.length > 0 ? values : undefined;
};

const normalizeVector3 = (value: unknown, fallback: Vector3): Vector3 => {
  const values = normalizeNumberArray(value);
  return values && values.length >= 3 ? [values[0], values[1], values[2]] : fallback;
};

const normalizeVector2 = (value: unknown): Vector2 | undefined => {
  const values = normalizeNumberArray(value);
  return values && values.length >= 2 ? [values[0], values[1]] : undefined;
};

const normalizeVector5 = (value: unknown): Vector5 | undefined => {
  const values = normalizeNumberArray(value);
  if (!values || values.length < 3) return undefined;
  const defaults: Vector5 = [0.9, 0.95, 0.001, 0.5, 2];
  return [
    values[0],
    values[1],
    values[2],
    Number.isFinite(values[3]) ? values[3] : defaults[3],
    Number.isFinite(values[4]) ? values[4] : defaults[4],
  ];
};

export const resolveJointProp = (
  state: JointParsingState,
  jointElem: Element,
  prop: string
): number[] | undefined => {
  const attr = jointElem.getAttribute(prop);
  if (attr) {
    return attr.trim().split(/\s+/).map(Number);
  }

  let className = jointElem.getAttribute("class");
  const visited = new Set();
  while (className && !visited.has(className)) {
    visited.add(className);
    const def = state.jointClassDefaults[className];
    const val = def?.[prop];
    const values = normalizeNumberArray(val);
    if (values) return values;
    const parentClass = def?.parentClass;
    className = typeof parentClass === "string" ? parentClass : null;
  }

  const val = state.jointDefaultScope[prop];
  const values = normalizeNumberArray(val);
  if (values) return values;

  return undefined;
};

export const resolveJointScalarProp = (
  state: JointParsingState,
  jointElem: Element,
  prop: string
): number | undefined => {
  const attr = jointElem.getAttribute(prop);
  if (attr) {
    return parseFloat(attr);
  }

  let className = jointElem.getAttribute("class");
  const visited = new Set();
  while (className && !visited.has(className)) {
    visited.add(className);
    const def = state.jointClassDefaults[className];
    const val = def?.[prop];
    if (typeof val === "number") return val;
    const parentClass = def?.parentClass;
    className = typeof parentClass === "string" ? parentClass : null;
  }

  const val = state.jointDefaultScope[prop];
  if (typeof val === "number") {
    return val;
  }

  return undefined;
};

export const resolveJointType = (
  state: JointParsingState,
  jointElem: Element
): "hinge" | "slide" | "ball" | "free" => {
  const className = jointElem.getAttribute("class");
  const typeAttr = jointElem.getAttribute("type");
  if (typeAttr) {
    return typeAttr as "hinge" | "slide" | "ball" | "free";
  }

  let currentClass = className;
  const visited = new Set();
  while (currentClass && !visited.has(currentClass)) {
    visited.add(currentClass);
    const def = state.jointClassDefaults[currentClass];
    const type = def?.type;
    if (type === "hinge" || type === "slide" || type === "ball" || type === "free") {
      return type;
    }
    const parentClass = def?.parentClass;
    currentClass = typeof parentClass === "string" ? parentClass : null;
  }

  const defaultType = state.jointDefaultScope.type;
  if (
    defaultType === "hinge" ||
    defaultType === "slide" ||
    defaultType === "ball" ||
    defaultType === "free"
  ) {
    return defaultType;
  }

  return "hinge";
};

export const createJointFromParsedData = (
  state: JointParsingState,
  name: string,
  kind: "free" | "ball" | "hinge" | "slide",
  parsedData: ParsedJointData,
  uuidOverride?: JointUUID | null
): Joint => {
  const uuid = (uuidOverride || generateUuid()) as JointUUID;
  const defaults = {
    uuid,
    name,
    kind,
    group: 0,
    stiffness: parsedData.stiffness ?? 0,
    damping: parsedData.damping ?? 0,
    frictionloss: parsedData.frictionloss ?? 0,
    springDamper: [0, 0] as Vector2,
    armature: parsedData.armature ?? 0,
    frictionSolverReference: [0.02, 1] as Vector2,
    frictionSolverImpedance: [0.9, 0.95, 0.001, 0.5, 2] as Vector5,
    ...(typeof parsedData.user === "string" && parsedData.user.trim() !== ""
      ? { user: parsedData.user.trim() }
      : {}),
  };

  if (kind === "free") {
    return {
      ...defaults,
      kind: "free",
    } as Joint;
  }

  const limitsDefaults = {
    useLimits: (() => {
      const lim = parsedData.limited;
      if (lim === false) return "false" as const;
      if (lim === true) return "true" as const;
      if (lim === "auto") return "auto" as const;
      return parsedData.range ? ("true" as const) : ("auto" as const);
    })(),
    contactMargin: 0.01,
    limitSolverReference: parsedData.limitSolverReference ?? ([0.02, 1] as Vector2),
    limitSolverImpedance:
      parsedData.limitSolverImpedance ?? ([0.9, 0.95, 0.001, 0.5, 2] as Vector5),
  };

  const toRad = state.angleUnit === "degree" ? Math.PI / 180 : 1;

  if (kind === "ball") {
    const position: Vector3 = normalizeVector3(parsedData.position, [0, 0, 0]);
    const maxRotation =
      parsedData.range && Array.isArray(parsedData.range) && parsedData.range.length === 2
        ? parsedData.range[1] * toRad
        : Math.PI;
    return {
      ...defaults,
      ...limitsDefaults,
      kind: "ball",
      position,
      max_rotation: maxRotation,
    } as Joint;
  }

  const position: Vector3 = normalizeVector3(parsedData.position, [0, 0, 0]);
  const axis: Vector3 = normalizeVector3(parsedData.axis, [0, 0, 1]);
  const range: [number, number] =
    parsedData.range && Array.isArray(parsedData.range) && parsedData.range.length === 2
      ? [parsedData.range[0] * toRad, parsedData.range[1] * toRad]
      : [-Math.PI, Math.PI];
  const actuationDefaults = {
    actfrclimited: 0,
    actfrcrange: [-Infinity, Infinity] as [number, number],
    actgravcomp: 0,
  };

  const rawRef = Number.isFinite(parsedData.ref) ? Number(parsedData.ref) * toRad : 0;
  return {
    ...defaults,
    ...limitsDefaults,
    ...actuationDefaults,
    kind,
    position,
    axis,
    ref: rawRef,
    initialRef: rawRef,
    restingPosition: parsedData.springref != null ? parsedData.springref * toRad : 0,
    range,
  } as Joint;
};

export const getResolvedJointElementName = (
  state: JointParsingState,
  jointElem: Element,
  fallbackIndex = 0
): string | null => {
  const explicitName = jointElem.getAttribute("name")?.trim();
  if (explicitName) return explicitName;

  if (jointElem.tagName?.toLowerCase() !== "freejoint") return null;

  const cached = state.resolvedJointElementNames.get(jointElem);
  if (cached) return cached;

  const parent = jointElem.parentElement;
  const parentBodyName =
    parent?.tagName?.toLowerCase() === "body" ? parent.getAttribute("name")?.trim() : "";
  const resolvedName = parentBodyName
    ? `${parentBodyName}_freejoint`
    : `freejoint_${fallbackIndex + 1}`;
  state.resolvedJointElementNames.set(jointElem, resolvedName);
  return resolvedName;
};

export const parseJoints = (
  parser: JointParsingState & {
    xmlDoc: Document;
    coerceUuidFromMjcfName: (name: string) => string | null;
  }
): Joint[] => {
  const parsedJoints: Joint[] = [];

  parser.xmlDoc.querySelectorAll("joint").forEach((j: Element) => {
    const name = j.getAttribute("name");
    if (!name) return;

    let axis = resolveJointProp(parser, j, "axis");
    const range = resolveJointProp(parser, j, "range");
    const type = resolveJointType(parser, j) as "hinge" | "slide" | "ball";

    const armature = resolveJointProp(parser, j, "armature");
    const damping = resolveJointProp(parser, j, "damping");
    const frictionloss = resolveJointProp(parser, j, "frictionloss");
    const stiffness = resolveJointScalarProp(parser, j, "stiffness");
    const ref = resolveJointScalarProp(parser, j, "ref");
    const springref = resolveJointScalarProp(parser, j, "springref");
    const limitSolverReference = normalizeVector2(resolveJointProp(parser, j, "solreflimit"));
    const limitSolverImpedance = normalizeVector5(resolveJointProp(parser, j, "solimplimit"));

    const hasValidAxis =
      Array.isArray(axis) &&
      axis.length === 3 &&
      axis.every((component) => Number.isFinite(component));

    if ((type === "hinge" || type === "slide") && !hasValidAxis) {
      axis = [0, 0, 1];
    }

    const pos = normalizeVector3(j.getAttribute("pos")?.trim().split(/\s+/).map(Number), [0, 0, 0]);

    const limitedAttr = j.getAttribute("limited");
    let limited: boolean | "auto" | undefined;
    if (limitedAttr != null && limitedAttr.trim() !== "") {
      const lr = limitedAttr.trim().toLowerCase();
      if (lr === "false" || lr === "0" || lr === "no") limited = false;
      else if (lr === "true" || lr === "1" || lr === "yes") limited = true;
      else if (lr === "auto") limited = "auto";
    }

    const parsedData = {
      axis: axis ? axis : undefined,
      range: range ? range : undefined,
      position: pos,
      armature: armature ? armature[0] : undefined,
      damping: damping ? damping[0] : undefined,
      frictionloss: frictionloss ? frictionloss[0] : undefined,
      stiffness,
      ref,
      springref,
      limited,
      limitSolverReference,
      limitSolverImpedance,
      user: j.getAttribute("user") || undefined,
    };

    const stableUuid = parser.coerceUuidFromMjcfName(name) as JointUUID | null;
    const joint = createJointFromParsedData(parser, name, type, parsedData, stableUuid);
    parsedJoints.push(joint);
  });

  parser.xmlDoc.querySelectorAll("freejoint").forEach((fj: Element, index: number) => {
    const explicitName = fj.getAttribute("name")?.trim();
    const name = getResolvedJointElementName(parser, fj, index) || `freejoint_${index + 1}`;
    const joint = createJointFromParsedData(parser, name, "free", {
      user: fj.getAttribute("user") || undefined,
    });
    const align = fj.getAttribute("align");
    const jointWithMetadata = joint as JointWithMjcfMetadata;
    if (explicitName) jointWithMetadata.mjcfName = explicitName;
    if (align != null && align.trim() !== "") jointWithMetadata.align = align.trim();
    parsedJoints.push(joint);
  });

  return parsedJoints;
};
